import {
  Ruler,
  Globe,
  IndianRupee,
  Banknote,
  TrendingUp,
  Percent,
  Maximize,
  Calendar,
  Receipt,
  LineChart,
} from 'lucide-react';
import type { Tool } from '@/registry/types';
import { UnitConverter } from './UnitConverter';
import { TimezoneConverter } from './TimezoneConverter';
import { CurrencyConverter } from './CurrencyConverter';
import { EmiCalculator } from './EmiCalculator';
import { MarginCalculator } from './MarginCalculator';
import { PercentageCalculator } from './PercentageCalculator';
import { AspectRatio } from './AspectRatio';
import { DateCalculator } from './DateCalculator';
import { TaxCalculator } from './TaxCalculator';
import { RoiCalculator } from './RoiCalculator';

export const calculatorTools: Tool[] = [
  {
    id: 'unit-converter',
    name: 'Unit converter',
    category: 'calculators',
    description: 'Length, mass, temperature, volume, area, speed, data, time.',
    keywords: ['unit', 'convert', 'length', 'mass', 'temperature', 'metric', 'imperial'],
    icon: Ruler,
    component: UnitConverter,
    inBrowser: true,
  },
  {
    id: 'timezone-converter',
    name: 'Timezone converter',
    category: 'calculators',
    description: 'Live timezone comparison across locations.',
    keywords: ['timezone', 'tz', 'time', 'meeting', 'schedule'],
    icon: Globe,
    component: TimezoneConverter,
    featured: true,
    inBrowser: true,
  },
  {
    id: 'currency-converter',
    name: 'Currency converter',
    category: 'calculators',
    description: 'Quick currency conversions across major denominations.',
    keywords: ['currency', 'usd', 'inr', 'eur', 'exchange', 'forex'],
    icon: IndianRupee,
    component: CurrencyConverter,
    inBrowser: true,
  },
  {
    id: 'emi-calculator',
    name: 'EMI calculator',
    category: 'calculators',
    description: 'Loan EMI, total interest, and principal/interest split.',
    keywords: ['emi', 'loan', 'mortgage', 'interest', 'principal'],
    icon: Banknote,
    component: EmiCalculator,
    featured: true,
    inBrowser: true,
  },
  {
    id: 'tax-calculator',
    name: 'Tax calculator',
    category: 'calculators',
    description: 'GST math + India income-tax slab estimate (FY 2025-26 new regime).',
    keywords: ['tax', 'gst', 'income tax', 'cess', 'slab', 'india'],
    icon: Receipt,
    component: TaxCalculator,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'roi-calculator',
    name: 'ROI calculator',
    category: 'calculators',
    description: 'Lump sum, SIP, and CAGR with growth-curve visualization.',
    keywords: ['roi', 'investment', 'sip', 'cagr', 'returns', 'wealth'],
    icon: LineChart,
    component: RoiCalculator,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'margin-calculator',
    name: 'Margin & markup',
    category: 'calculators',
    description: 'Selling price, margin, and markup from cost.',
    keywords: ['margin', 'markup', 'price', 'profit', 'cost'],
    icon: TrendingUp,
    component: MarginCalculator,
    inBrowser: true,
  },
  {
    id: 'percentage-calculator',
    name: 'Percentage calculator',
    category: 'calculators',
    description: 'X% of Y, percentage change, increase, decrease, and reverse.',
    keywords: ['percent', 'percentage', '%', 'change', 'increase', 'decrease'],
    icon: Percent,
    component: PercentageCalculator,
    inBrowser: true,
  },
  {
    id: 'aspect-ratio',
    name: 'Aspect ratio calculator',
    category: 'calculators',
    description: 'Resize images and screens while preserving aspect ratio.',
    keywords: ['aspect', 'ratio', 'image', 'screen', 'resize'],
    icon: Maximize,
    component: AspectRatio,
    inBrowser: true,
  },
  {
    id: 'date-calculator',
    name: 'Date calculator',
    category: 'calculators',
    description: 'Days between, add/subtract, age, weekday lookups.',
    keywords: ['date', 'days', 'between', 'age', 'add', 'subtract'],
    icon: Calendar,
    component: DateCalculator,
    inBrowser: true,
  },
];
